import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Plus, Edit, Trash2, X, AlertCircle, CheckCircle,
  Users, Tag, Percent, Calculator, ClipboardList, ChevronDown,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import AppLayout from '../layout/AppLayout';
import MobileHeader from '../layout/MobileHeader';
import { organizationService } from '../services/OrganizationService';
import {
  discountService,
  DiscountAgreement,
  DiscountAgreementCreate,
  DiscountSummary,
  AuditLogEntry,
  PriceResolveResult,
  B2BDefault,
} from '../services/DiscountService';

/* ─── constants ──────────────────────────────────────────────── */

const SCOPE_TYPE_KEYS = [
  { value: 'brand',        labelKey: 'discounts.scopeTypes.brand',        placeholderKey: 'discounts.simulator.brandPlaceholder',        enabled: true  },
  { value: 'category',     labelKey: 'discounts.scopeTypes.category',     placeholderKey: 'discounts.simulator.categoryPlaceholder',     enabled: false },
  { value: 'part_name',    labelKey: 'discounts.scopeTypes.part_name',    placeholderKey: 'discounts.simulator.partNamePlaceholder',     enabled: false },
  { value: 'oe_sku',       labelKey: 'discounts.scopeTypes.oe_sku',       placeholderKey: 'discounts.simulator.oeSkuPlaceholder',        enabled: false },
  { value: 'supplier_sku', labelKey: 'discounts.scopeTypes.supplier_sku', placeholderKey: 'discounts.simulator.supplierSkuPlaceholder',  enabled: false },
] as const;

const EMPTY_FORM: DiscountAgreementCreate = {
  customer_name: '', customer_type: 'b2b', scope_type: 'brand',
  scope_value: '', discount_type: 'percentage', discount_value: 0,
  currency: null, expiry_date: null, effective_date: null,
};

type Tab = 'agreements' | 'simulator' | 'audit';

/* ─── helpers ────────────────────────────────────────────────── */

function daysUntil(d: string | null) {
  if (!d) return null;
  return Math.ceil((new Date(d).getTime() - Date.now()) / 86_400_000);
}
function fmt(d: string | null) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}
const INPUT = 'w-full px-3 py-2 text-sm border border-[#efebe2] rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#6866ff]/30 focus:border-[#6866ff] transition-colors';

/* ════════════════════════════════════════════════════════════════ */

const DiscountDashboard: React.FC = () => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin';

  const [tab, setTab] = useState<Tab>('agreements');

  /* ── agreements ── */
  const [agreements, setAgreements] = useState<DiscountAgreement[]>([]);
  const [summary, setSummary] = useState<DiscountSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(false);

  /* ── modal ── */
  const [showModal, setShowModal] = useState(false);
  const [editTarget, setEditTarget] = useState<DiscountAgreement | null>(null);
  const [form, setForm] = useState<DiscountAgreementCreate>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  /* ── delete ── */
  const [deleteTarget, setDeleteTarget] = useState<DiscountAgreement | null>(null);
  const [deleting, setDeleting] = useState(false);

  /* ── audit ── */
  const [auditLogs, setAuditLogs] = useState<AuditLogEntry[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);

  /* ── simulator ── */
  const [sim, setSim] = useState({
    customer_name: '', retail_price: '', currency: 'USD',
    part_brand: '', part_category: '', part_name: '', oe_sku: '', supplier_sku: '',
  });
  const [simResult, setSimResult] = useState<PriceResolveResult | null>(null);
  const [simLoading, setSimLoading] = useState(false);
  const [simError, setSimError] = useState<string | null>(null);

  /* ── B2B default ── */
  const [b2bDefault, setB2bDefault] = useState<B2BDefault | null>(null);
  const [showB2bForm, setShowB2bForm] = useState(false);
  const [b2bForm, setB2bForm] = useState({ type: 'percentage', value: '', currency: '' });
  const [b2bSaving, setB2bSaving] = useState(false);

  /* ── catalog brands ── */
  const [availableBrands, setAvailableBrands] = useState<string[]>([]);
  const [orgCurrency, setOrgCurrency] = useState<string>('USD');

  /* ── fetch ── */
  const fetchAll = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [agRes, sumRes, b2bRes] = await Promise.all([
        discountService.listAgreements(),
        discountService.getSummary(),
        discountService.getB2BDefault(),
      ]);
      setAgreements(agRes.data.agreements);
      setSummary(sumRes.data);
      setB2bDefault(b2bRes.data);
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? t('discounts.errors.loadFailed'));
    } finally { setLoading(false); }
  }, [t]);

  // Load org catalog brands once on mount
  useEffect(() => {
    organizationService.getOrganizationSettings()
      .then(res => {
        const configs: any[] = res.data?.catalogs_config ?? [];
        const names = configs.map((c: any) => c.catalog_name).filter(Boolean);
        setAvailableBrands(names.length > 0 ? names : []);
        if (res.data?.currency) {
          setOrgCurrency(res.data.currency);
          setSim(prev => ({ ...prev, currency: res.data.currency }));
        }
      })
      .catch(() => { /* silently fallback to empty — UI shows free text */ });
  }, []);

  const fetchAudit = useCallback(async () => {
    setAuditLoading(true);
    try {
      const res = await discountService.getAuditTrail();
      setAuditLogs(res.data.logs);
    } catch { /* ignore */ } finally { setAuditLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => { if (tab === 'audit') fetchAudit(); }, [tab, fetchAudit]);

  /* ── modal open ── */
  const openCreate = () => { setEditTarget(null); setForm(EMPTY_FORM); setFormError(null); setShowModal(true); };
  const openEdit = (ag: DiscountAgreement) => {
    setEditTarget(ag);
    setForm({
      customer_name: ag.customer_name, customer_type: ag.customer_type,
      scope_type: ag.scope_type, scope_value: ag.scope_value,
      discount_type: ag.discount_type, discount_value: ag.discount_value,
      currency: ag.currency, expiry_date: ag.expiry_date, effective_date: ag.effective_date,
    });
    setFormError(null); setShowModal(true);
  };

  /* ── save ── */
  const handleSave = async () => {
    if (!form.customer_name.trim()) { setFormError(t('discounts.errors.customerRequired')); return; }
    if (!form.scope_value.trim()) { setFormError(t('discounts.errors.scopeValueRequired')); return; }
    if (form.discount_value < 0) { setFormError(t('discounts.errors.discountNonNegative')); return; }
    if (form.discount_type === 'fixed' && !form.currency) { setFormError(t('discounts.errors.currencyRequired')); return; }
    setSaving(true); setFormError(null);
    try {
      if (editTarget) await discountService.updateAgreement(editTarget.id, form);
      else await discountService.createAgreement(form);
      setShowModal(false); fetchAll();
    } catch (e: any) {
      const status = e?.response?.status;
      if (status === 409) setFormError(t('discounts.errors.conflict'));
      else if (status === 422) setFormError(e?.response?.data?.detail ?? t('discounts.errors.saveFailed'));
      else setFormError(e?.response?.data?.detail ?? t('discounts.errors.saveFailed'));
    } finally { setSaving(false); }
  };

  /* ── delete ── */
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try { await discountService.deleteAgreement(deleteTarget.id); setDeleteTarget(null); fetchAll(); }
    catch { /* ignore */ } finally { setDeleting(false); }
  };

  /* ── simulator ── */
  const handleSimulate = async () => {
    if (!sim.customer_name || !sim.retail_price) { setSimError(t('discounts.simulator.customerRequired')); return; }
    setSimLoading(true); setSimError(null); setSimResult(null);
    try {
      const res = await discountService.resolvePrice({
        customer_name: sim.customer_name,
        retail_price: parseFloat(sim.retail_price),
        currency: sim.currency,
        part_brand: sim.part_brand || undefined,
        part_category: sim.part_category || undefined,
        part_name: sim.part_name || undefined,
        oe_sku: sim.oe_sku || undefined,
        supplier_sku: sim.supplier_sku || undefined,
      });
      setSimResult(res.data);
    } catch (e: any) { setSimError(e?.response?.data?.detail ?? t('discounts.simulator.failed')); }
    finally { setSimLoading(false); }
  };

  /* ── B2B default save ── */
  const handleB2bSave = async () => {
    if (!b2bForm.value) return;
    setB2bSaving(true);
    try {
      await discountService.setB2BDefault({
        b2b_default_discount_type: b2bForm.type,
        b2b_default_discount_value: parseFloat(b2bForm.value),
        b2b_default_currency: b2bForm.currency || undefined,
      });
      setShowB2bForm(false); fetchAll();
    } catch { /* ignore */ } finally { setB2bSaving(false); }
  };

  /* ── chips ── */
  const StatusChip = ({ ag }: { ag: DiscountAgreement }) => {
    const base = 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium';
    if (!ag.is_active) return <span className={`${base} bg-gray-100 text-gray-500`}><X size={10} />{t('discounts.status.inactive')}</span>;
    const d = daysUntil(ag.expiry_date);
    if (d !== null && d < 0) return <span className={`${base} bg-red-100 text-red-600`}><AlertCircle size={10} />{t('discounts.status.expired')}</span>;
    return <span className={`${base} bg-green-100 text-green-700`}><CheckCircle size={10} />{t('discounts.status.active')}</span>;
  };

  const b2bDefaultSuffix = summary?.b2b_default_discount_type === 'percentage'
    ? '%'
    : ` ${summary?.b2b_default_currency ?? ''}`;

  const displayed = showInactive ? agreements : agreements.filter(a => a.is_active);

  /* ════════════════════════════════════════════════════════════ */
  return (
    <AppLayout>
      <MobileHeader title={t('discounts.title')} />
      <div className="flex flex-col h-full overflow-hidden">

        {/* ── top bar ── */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#efebe2] bg-white flex-shrink-0">
          <div>
            <h1 className="text-xl font-semibold text-[#201e1a]">{t('discounts.title')}</h1>
            <p className="text-sm text-[#999999] mt-0.5">{t('discounts.subtitle')}</p>
          </div>
          {isAdmin && tab === 'agreements' && (
            <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 bg-[#6866ff] hover:bg-[#5553e0] text-white text-sm font-medium rounded-lg transition-colors">
              <Plus size={16} /> {t('discounts.newAgreement')}
            </button>
          )}
        </div>

        {/* ── tabs ── */}
        <div className="flex border-b border-[#efebe2] bg-white px-6 flex-shrink-0">
          {([
            { id: 'agreements', labelKey: 'discounts.tabs.agreements', icon: Tag },
            ...(isAdmin ? [{ id: 'simulator', labelKey: 'discounts.tabs.simulator', icon: Calculator }] : []),
            { id: 'audit', labelKey: 'discounts.tabs.audit', icon: ClipboardList },
          ] as { id: Tab; labelKey: string; icon: any }[]).map(({ id, labelKey, icon: Icon }) => (
            <button key={id} onClick={() => setTab(id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${tab === id ? 'border-[#6866ff] text-[#6866ff]' : 'border-transparent text-[#999999] hover:text-[#201e1a]'}`}>
              <Icon size={14} />{t(labelKey)}
            </button>
          ))}
        </div>

        {/* ── body ── */}
        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6 bg-[#f6f4f0]">

          {/* ════ AGREEMENTS TAB ════ */}
          {tab === 'agreements' && (<>

            {/* metrics strip */}
            {summary && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <MetricCard icon={<CheckCircle size={20} className="text-[#6866ff]" />} label={t('discounts.metrics.activeAgreements')} value={summary.total_active} />
                <MetricCard icon={<Users size={20} className="text-[#6866ff]" />} label={t('discounts.metrics.customersCovered')} value={summary.customers_covered} />
                <MetricCard
                  icon={<Percent size={20} className="text-[#6866ff]" />}
                  label={t('discounts.metrics.b2bDefault')}
                  value={summary.b2b_default_discount != null
                    ? `${summary.b2b_default_discount}${b2bDefaultSuffix}`
                    : t('discounts.metrics.notConfigured')}
                />
              </div>
            )}

            {/* B2B default config (admin) */}
            {isAdmin && (
              <div className="bg-white border border-[#efebe2] rounded-xl px-5 py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-[#201e1a]">{t('discounts.b2bDefault.title')}</p>
                    <p className="text-xs text-[#999999] mt-0.5">
                      {b2bDefault?.b2b_default_discount_value != null
                        ? t('discounts.b2bDefault.description', {
                            value: b2bDefault.b2b_default_discount_value,
                            suffix: b2bDefault.b2b_default_discount_type === 'percentage' ? '%' : ` ${b2bDefault.b2b_default_currency ?? ''}`,
                          })
                        : t('discounts.b2bDefault.noConfig')}
                    </p>
                  </div>
                  <button onClick={() => { setB2bForm({ type: b2bDefault?.b2b_default_discount_type ?? 'percentage', value: String(b2bDefault?.b2b_default_discount_value ?? ''), currency: b2bDefault?.b2b_default_currency ?? '' }); setShowB2bForm(true); }}
                    className="px-3 py-1.5 text-xs font-medium border border-[#efebe2] rounded-lg hover:bg-[#f6f4f0] transition-colors">
                    {b2bDefault?.b2b_default_discount_value != null ? t('discounts.b2bDefault.edit') : t('discounts.b2bDefault.configure')}
                  </button>
                </div>
              </div>
            )}

            {/* precedence callout */}
            <div className="flex items-start gap-3 bg-[#f0efff] border border-[#cecdff] rounded-lg px-4 py-3 text-sm text-[#4a49b8]">
              <Tag size={16} className="mt-0.5 flex-shrink-0" />
              <span>{t('discounts.precedenceBanner')}</span>
            </div>

            {/* show inactive toggle */}
            <div className="flex items-center gap-2 text-sm text-[#999999]">
              <input type="checkbox" id="showInactive" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} className="rounded" />
              <label htmlFor="showInactive">{t('discounts.showInactive')}</label>
            </div>

            {loading ? (
              <div className="flex justify-center py-16"><div className="w-8 h-8 border-4 border-[#6866ff] border-t-transparent rounded-full animate-spin" /></div>
            ) : error ? (
              <div className="flex items-center gap-2 text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3"><AlertCircle size={16} />{error}</div>
            ) : displayed.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-[#999999]">
                <Tag size={40} className="mb-3 opacity-40" />
                <p className="font-medium">{t('discounts.table.noData')}</p>
                {isAdmin && <p className="text-sm mt-1">{t('discounts.table.noDataHint')}</p>}
              </div>
            ) : (
              <div className="bg-white border border-[#efebe2] rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#efebe2] bg-[#f6f4f0] text-[#999999] text-xs uppercase tracking-wide">
                      <th className="px-4 py-3 text-left">{t('discounts.table.customer')}</th>
                      <th className="px-4 py-3 text-left">{t('discounts.table.type')}</th>
                      <th className="px-4 py-3 text-left">{t('discounts.table.scope')}</th>
                      <th className="px-4 py-3 text-left">{t('discounts.table.value')}</th>
                      <th className="px-4 py-3 text-left">{t('discounts.table.discount')}</th>
                      <th className="px-4 py-3 text-left">{t('discounts.table.expiry')}</th>
                      <th className="px-4 py-3 text-left">{t('discounts.table.status')}</th>
                      {isAdmin && <th className="px-4 py-3 text-right">{t('discounts.table.actions')}</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#efebe2]">
                    {displayed.map(ag => {
                      const days = daysUntil(ag.expiry_date);
                      const expiringSoon = days !== null && days >= 0 && days <= 30;
                      return (
                        <tr key={ag.id} className={`hover:bg-[#f6f4f0] transition-colors ${expiringSoon ? 'border-l-4 border-l-amber-400' : ''} ${!ag.is_active ? 'opacity-50' : ''}`}>
                          <td className="px-4 py-3 font-medium text-[#201e1a]">{ag.customer_name}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${ag.customer_type === 'b2b' ? 'bg-blue-100 text-blue-700' : 'bg-pink-100 text-pink-700'}`}>
                              {t(`discounts.customerTypes.${ag.customer_type}`)}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-[#f0efff] text-[#6866ff]">
                              {t(`discounts.scopeTypes.${ag.scope_type}`, { defaultValue: ag.scope_type })}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-[#201e1a]">{ag.scope_value}</td>
                          <td className="px-4 py-3 font-semibold text-green-600">
                            {ag.discount_type === 'percentage' ? `${ag.discount_value}%` : `${ag.discount_value} ${ag.currency ?? ''}`}
                          </td>
                          <td className={`px-4 py-3 ${expiringSoon ? 'text-amber-600 font-medium' : 'text-[#999999]'}`}>
                            {fmt(ag.expiry_date)}{expiringSoon && <span className="ml-1 text-xs">{t('discounts.table.daysRemaining', { days })}</span>}
                          </td>
                          <td className="px-4 py-3"><StatusChip ag={ag} /></td>
                          {isAdmin && (
                            <td className="px-4 py-3 text-right">
                              <div className="flex items-center justify-end gap-2">
                                <button onClick={() => openEdit(ag)} className="p-1.5 rounded hover:bg-[#f6f4f0] text-[#999999] hover:text-[#201e1a] transition-colors"><Edit size={14} /></button>
                                <button onClick={() => setDeleteTarget(ag)} className="p-1.5 rounded hover:bg-red-50 text-[#999999] hover:text-red-600 transition-colors"><Trash2 size={14} /></button>
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>)}

          {/* ════ PRICE SIMULATOR TAB (admin only) ════ */}
          {tab === 'simulator' && isAdmin && (
            <div className="space-y-6 max-w-2xl">
              <div className="bg-white border border-[#efebe2] rounded-xl p-6 space-y-4">
                <h2 className="text-base font-semibold text-[#201e1a]">{t('discounts.simulator.title')}</h2>
                <p className="text-sm text-[#999999]">{t('discounts.simulator.description')}</p>

                <div className="grid grid-cols-2 gap-4">
                  <FF label={t('discounts.simulator.customerName')}><input className={INPUT} value={sim.customer_name} onChange={e => setSim({...sim, customer_name: e.target.value})} placeholder={t('discounts.simulator.customerNamePlaceholder')} /></FF>
                  <FF label={t('discounts.simulator.retailPrice')}><input className={INPUT} type="number" min={0} step={0.01} value={sim.retail_price} onChange={e => setSim({...sim, retail_price: e.target.value})} placeholder="120.00" /></FF>
                  <FF label={t('discounts.simulator.currency')}>
                    <div className={`${INPUT} bg-[#f6f4f0] text-[#999999] cursor-not-allowed flex items-center justify-between`}>
                      <span>{orgCurrency}</span>
                      <span className="text-xs text-[#bbbbbb]">{t('discounts.simulator.currencyFromSettings')}</span>
                    </div>
                  </FF>
                  <FF label={t('discounts.simulator.brand')}><BrandSelect brands={availableBrands} value={sim.part_brand} onChange={v => setSim({...sim, part_brand: v})} placeholder={t('discounts.simulator.brandPlaceholder')} /></FF>
                  <FF label={t('discounts.simulator.category')}><input className={INPUT} value={sim.part_category} onChange={e => setSim({...sim, part_category: e.target.value})} placeholder={t('discounts.simulator.categoryPlaceholder')} /></FF>
                  <FF label={t('discounts.simulator.partName')}><input className={INPUT} value={sim.part_name} onChange={e => setSim({...sim, part_name: e.target.value})} placeholder={t('discounts.simulator.partNamePlaceholder')} /></FF>
                  <FF label={t('discounts.simulator.oeSku')}><input className={INPUT} value={sim.oe_sku} onChange={e => setSim({...sim, oe_sku: e.target.value})} placeholder={t('discounts.simulator.oeSkuPlaceholder')} /></FF>
                  <FF label={t('discounts.simulator.supplierSku')}><input className={INPUT} value={sim.supplier_sku} onChange={e => setSim({...sim, supplier_sku: e.target.value})} placeholder={t('discounts.simulator.supplierSkuPlaceholder')} /></FF>
                </div>

                {simError && <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg px-3 py-2"><AlertCircle size={14} />{simError}</div>}

                <button onClick={handleSimulate} disabled={simLoading}
                  className="px-5 py-2.5 bg-[#6866ff] text-white text-sm font-medium rounded-lg hover:bg-[#5553e0] disabled:opacity-60 transition-colors">
                  {simLoading ? t('discounts.simulator.resolving') : t('discounts.simulator.resolve')}
                </button>
              </div>

              {simResult && (
                <div className="bg-white border border-[#efebe2] rounded-xl p-6 space-y-3">
                  <h3 className="text-sm font-semibold text-[#201e1a]">{t('discounts.simulator.result.title')}</h3>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <ResultRow label={t('discounts.simulator.result.retailPrice')} value={`${simResult.retail_price} ${simResult.currency}`} />
                    <ResultRow label={t('discounts.simulator.result.appliedRule')} value={simResult.scope_type
                      ? t('discounts.simulator.result.appliedRuleValue', { scope: simResult.scope_type.replace(/_/g, ' '), value: simResult.scope_value })
                      : t('discounts.simulator.result.appliedRuleNone')} />
                    <ResultRow label={t('discounts.simulator.result.discount')} value={simResult.discount_type === 'percentage' ? `${simResult.discount_value}%` : simResult.discount_type === 'fixed' ? `${simResult.discount_value} ${simResult.currency}` : '—'} />
                    <ResultRow label={t('discounts.simulator.result.discountAmount')} value={`${simResult.discount_amount} ${simResult.currency}`} />
                    <ResultRow label={t('discounts.simulator.result.finalPrice')} value={`${simResult.final_price} ${simResult.currency}`} highlight />
                    <ResultRow label={t('discounts.simulator.result.agreementId')} value={simResult.agreement_id ? `#${simResult.agreement_id}` : t('discounts.simulator.result.agreementIdNone')} />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ════ AUDIT TRAIL TAB ════ */}
          {tab === 'audit' && (
            <div className="space-y-4">
              <div className="bg-white border border-[#efebe2] rounded-xl overflow-hidden">
                {auditLoading ? (
                  <div className="flex justify-center py-12"><div className="w-6 h-6 border-4 border-[#6866ff] border-t-transparent rounded-full animate-spin" /></div>
                ) : auditLogs.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-[#999999]">
                    <ClipboardList size={36} className="mb-2 opacity-40" />
                    <p>{t('discounts.audit.noData')}</p>
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[#efebe2] bg-[#f6f4f0] text-[#999999] text-xs uppercase tracking-wide">
                        <th className="px-4 py-3 text-left">{t('discounts.audit.when')}</th>
                        <th className="px-4 py-3 text-left">{t('discounts.audit.actor')}</th>
                        <th className="px-4 py-3 text-left">{t('discounts.audit.action')}</th>
                        <th className="px-4 py-3 text-left">{t('discounts.audit.entity')}</th>
                        <th className="px-4 py-3 text-left">{t('discounts.audit.field')}</th>
                        <th className="px-4 py-3 text-left">{t('discounts.audit.change')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#efebe2]">
                      {auditLogs.map(l => (
                        <tr key={l.id} className="hover:bg-[#f6f4f0]">
                          <td className="px-4 py-3 text-[#999999] whitespace-nowrap">{new Date(l.timestamp).toLocaleString()}</td>
                          <td className="px-4 py-3 text-[#201e1a]">{l.actor_display_name ?? l.actor_user_id}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${l.action === 'created' ? 'bg-green-100 text-green-700' : l.action === 'deleted' ? 'bg-red-100 text-red-600' : 'bg-blue-100 text-blue-700'}`}>
                              {t(`discounts.audit.actions.${l.action}`, { defaultValue: l.action })}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-[#999999]">{l.entity_type} #{l.entity_id}</td>
                          <td className="px-4 py-3 text-[#999999]">{l.field_changed ?? '—'}</td>
                          <td className="px-4 py-3 text-xs text-[#999999] max-w-xs truncate">
                            {l.old_value && <span className="line-through mr-1">{l.old_value.slice(0, 40)}</span>}
                            {l.new_value && <span>{l.new_value.slice(0, 40)}</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}

        </div>
      </div>

      {/* ══ Create / Edit Modal ══ */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#efebe2]">
              <h2 className="text-base font-semibold text-[#201e1a]">{editTarget ? t('discounts.modal.editTitle') : t('discounts.modal.createTitle')}</h2>
              <button onClick={() => setShowModal(false)} className="p-1.5 rounded hover:bg-[#f6f4f0] text-[#999999]"><X size={16} /></button>
            </div>
            <div className="px-6 py-5 space-y-4 overflow-y-auto">
              <FF label={t('discounts.modal.customerName')}><input type="text" value={form.customer_name} onChange={e => setForm({...form, customer_name: e.target.value})} placeholder={t('discounts.modal.customerNamePlaceholder')} className={INPUT} /></FF>
              <FF label={t('discounts.modal.customerType')}>
                <select value={form.customer_type} onChange={e => setForm({...form, customer_type: e.target.value as any})} className={INPUT}>
                  <option value="b2b">{t('discounts.customerTypes.b2b')}</option>
                  <option value="b2c">{t('discounts.customerTypes.b2c')}</option>
                </select>
              </FF>
              <FF label={t('discounts.modal.scopeType')}>
                <select value={form.scope_type} onChange={e => setForm({...form, scope_type: e.target.value as any, scope_value: ''})} className={INPUT}>
                  {SCOPE_TYPE_KEYS.map(s => (
                    <option key={s.value} value={s.value} disabled={!s.enabled}>
                      {t(s.labelKey)}
                    </option>
                  ))}
                </select>
              </FF>
              <FF label={t('discounts.modal.scopeValue')}>
                {form.scope_type === 'brand'
                  ? <BrandSelect brands={availableBrands} value={form.scope_value} onChange={(v: string) => setForm({...form, scope_value: v})} placeholder={t('discounts.modal.customerNamePlaceholder')} />
                  : <input type="text" value={form.scope_value} onChange={e => setForm({...form, scope_value: e.target.value})}
                      placeholder={t(SCOPE_TYPE_KEYS.find(s => s.value === form.scope_type)?.placeholderKey ?? 'discounts.simulator.brandPlaceholder')} className={INPUT} />
                }
              </FF>
              <div className="grid grid-cols-2 gap-3">
                <FF label={t('discounts.modal.discountType')}>
                  <select value={form.discount_type} onChange={e => setForm({...form, discount_type: e.target.value as any})} className={INPUT}>
                    <option value="percentage">{t('discounts.modal.discountTypePercentage')}</option>
                    <option value="fixed">{t('discounts.modal.discountTypeFixed')}</option>
                  </select>
                </FF>
                <FF label={t('discounts.modal.discountValue')}>
                  <input type="number" min={0} step={0.01} value={form.discount_value}
                    onChange={e => setForm({...form, discount_value: parseFloat(e.target.value) || 0})} className={INPUT} />
                </FF>
              </div>
              {form.discount_type === 'fixed' && (
                <FF label={t('discounts.modal.currency')}>
                  <input type="text" value={form.currency ?? ''} onChange={e => setForm({...form, currency: e.target.value || null})} placeholder={t('discounts.modal.currencyPlaceholder')} className={INPUT} />
                </FF>
              )}
              <FF label={t('discounts.modal.effectiveDate')}>
                <input type="date" value={form.effective_date ?? ''} onChange={e => setForm({...form, effective_date: e.target.value || null})} className={INPUT} />
              </FF>
              <FF label={t('discounts.modal.expiryDate')}>
                <input type="date" value={form.expiry_date ?? ''} onChange={e => setForm({...form, expiry_date: e.target.value || null})} className={INPUT} />
              </FF>
              {formError && (
                <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg px-3 py-2"><AlertCircle size={14} />{formError}</div>
              )}
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#efebe2] bg-[#f6f4f0]">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-sm text-[#201e1a] rounded-lg border border-[#efebe2] hover:bg-white transition-colors">{t('discounts.modal.cancel')}</button>
              <button onClick={handleSave} disabled={saving} className="px-4 py-2 text-sm font-medium bg-[#6866ff] text-white rounded-lg hover:bg-[#5553e0] disabled:opacity-60 transition-colors">
                {saving ? t('discounts.modal.saving') : editTarget ? t('discounts.modal.save') : t('discounts.modal.create')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══ Delete confirm ══ */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
            <div className="px-6 py-5">
              <h2 className="text-base font-semibold text-[#201e1a] mb-2">{t('discounts.delete.title')}</h2>
              <p className="text-sm text-[#999999]"
                dangerouslySetInnerHTML={{ __html: t('discounts.delete.description', {
                  customer: deleteTarget.customer_name,
                  scope: deleteTarget.scope_type,
                  value: deleteTarget.scope_value,
                }) }} />
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#efebe2] bg-[#f6f4f0]">
              <button onClick={() => setDeleteTarget(null)} className="px-4 py-2 text-sm text-[#201e1a] rounded-lg border border-[#efebe2] hover:bg-white transition-colors">{t('discounts.delete.cancel')}</button>
              <button onClick={handleDelete} disabled={deleting} className="px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-60 transition-colors">
                {deleting ? t('discounts.delete.confirming') : t('discounts.delete.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══ B2B default modal ══ */}
      {showB2bForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#efebe2]">
              <h2 className="text-base font-semibold text-[#201e1a]">{t('discounts.b2bDefault.title')}</h2>
              <button onClick={() => setShowB2bForm(false)} className="p-1.5 rounded hover:bg-[#f6f4f0] text-[#999999]"><X size={16} /></button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <FF label={t('discounts.b2bDefault.discountType')}>
                <select value={b2bForm.type} onChange={e => setB2bForm({...b2bForm, type: e.target.value})} className={INPUT}>
                  <option value="percentage">{t('discounts.modal.discountTypePercentage')}</option>
                  <option value="fixed">{t('discounts.modal.discountTypeFixed')}</option>
                </select>
              </FF>
              <FF label={t('discounts.b2bDefault.discountValue')}>
                <input type="number" min={0} step={0.01} value={b2bForm.value} onChange={e => setB2bForm({...b2bForm, value: e.target.value})} className={INPUT} />
              </FF>
              {b2bForm.type === 'fixed' && (
                <FF label={t('discounts.b2bDefault.currency')}><input type="text" value={b2bForm.currency} onChange={e => setB2bForm({...b2bForm, currency: e.target.value})} placeholder="USD" className={INPUT} /></FF>
              )}
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#efebe2] bg-[#f6f4f0]">
              <button onClick={() => setShowB2bForm(false)} className="px-4 py-2 text-sm text-[#201e1a] rounded-lg border border-[#efebe2] hover:bg-white transition-colors">{t('discounts.modal.cancel')}</button>
              <button onClick={handleB2bSave} disabled={b2bSaving} className="px-4 py-2 text-sm font-medium bg-[#6866ff] text-white rounded-lg hover:bg-[#5553e0] disabled:opacity-60 transition-colors">
                {b2bSaving ? t('discounts.b2bDefault.saving') : t('discounts.b2bDefault.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
};

/* ─── small helpers ──────────────────────────────────────────── */

const FF: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="space-y-1.5">
    <label className="block text-xs font-medium text-[#999999] uppercase tracking-wide">{label}</label>
    {children}
  </div>
);

const MetricCard: React.FC<{ icon: React.ReactNode; label: string; value: string | number }> = ({ icon, label, value }) => (
  <div className="bg-white border border-[#efebe2] rounded-xl px-5 py-4 flex items-center gap-4">
    <div className="p-2 rounded-lg bg-[#f0efff]">{icon}</div>
    <div>
      <p className="text-xs text-[#999999] font-medium uppercase tracking-wide">{label}</p>
      <p className="text-xl font-bold text-[#201e1a] mt-0.5">{value}</p>
    </div>
  </div>
);

const ResultRow: React.FC<{ label: string; value: string; highlight?: boolean }> = ({ label, value, highlight }) => (
  <div className={`flex justify-between items-center py-2 border-b border-[#efebe2] last:border-0 ${highlight ? 'font-semibold text-[#201e1a]' : 'text-[#999999]'}`}>
    <span className="text-xs uppercase tracking-wide">{label}</span>
    <span className={highlight ? 'text-base text-green-600' : 'text-sm'}>{value}</span>
  </div>
);

/* ─── BrandSelect ────────────────────────────────────────────── */

const BrandSelect: React.FC<{
  brands: string[];
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}> = ({ brands, value, onChange, placeholder }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = brands.filter(b => b.toLowerCase().includes(query.toLowerCase()));

  return (
    <div ref={ref} className="relative">
      <div className={`${INPUT} flex items-center justify-between cursor-pointer`} onClick={() => setOpen(o => !o)}>
        <span className={value ? 'text-[#201e1a]' : 'text-[#999999]'}>{value || placeholder}</span>
        <ChevronDown size={14} className={`text-[#999999] transition-transform ${open ? 'rotate-180' : ''}`} />
      </div>
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-[#efebe2] rounded-lg shadow-lg overflow-hidden">
          <div className="p-2 border-b border-[#efebe2]">
            <input
              autoFocus
              className="w-full px-2 py-1.5 text-sm border border-[#efebe2] rounded focus:outline-none focus:border-[#6866ff]"
              placeholder={t('discounts.simulator.searchBrands')}
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
          </div>
          <ul className="max-h-52 overflow-y-auto">
            {filtered.length === 0
              ? <li className="px-3 py-2 text-sm text-[#999999]">{t('discounts.simulator.noBrandsFound')}</li>
              : filtered.map(b => (
                <li key={b}
                  className={`px-3 py-2 text-sm cursor-pointer hover:bg-[#f0efff] ${value === b ? 'bg-[#f0efff] text-[#6866ff] font-medium' : 'text-[#201e1a]'}`}
                  onClick={() => { onChange(b); setOpen(false); setQuery(''); }}>
                  {b}
                </li>
              ))
            }
          </ul>
        </div>
      )}
    </div>
  );
};

export default DiscountDashboard;
